/* eslint-disable react/prefer-stateless-function */

// Modules
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  PRAECE_ADMIN_ROLES,
  PRAECE_OPERATIONS_ROLES,
  PRAECE_LEAD_ROLES,
  isMobile,
  menuWidth
} from 'constants';
import { getFolderOpenUrl } from 'utils/google-drive-utils';
import { preventDefault } from 'utils/event-handlers';
import AddCompany from 'components/models/company/add-company-popup';
import AddPerson from 'components/models/person/add-person-popup';
import AddTicket from 'components/models/ticket/forms/add-ticket-popup';
import AddTimeReport from 'components/models/time-report/forms/add-time-report-popup';
import PortalChangesMenuAlert from
  'components/main-menu/components/portal-changes-menu-alert';

import CurrentUser from './components/current-user.js';
import ExpenseMenuAlert from './components/expense-menu-alert.js';
import InvoiceMenuAlert from './components/invoice-menu-alert.js';
import MenuItem from './components/menu-item';
import TicketMenuAlert from './components/ticket-menu-alert.js';
import { useCurrentUser, usePageData, PAGE_DATA_KEYS } from 'hooks';
import TeamPhoneButton from 'components/widgets/buttons/team-phone-button';
import _ from 'lodash';

function MyTeamPhone() {
  const { currentUser } = useCurrentUser();
  const teams = usePageData(PAGE_DATA_KEYS.teams);
  const teamId = _.get(currentUser, 'team.id', currentUser?.team);
  const team = _.find(teams, { id: teamId });
  return <TeamPhoneButton team={team} title="Access your team phone" inMenu />;
}

// Component
export default class MainMenu extends React.Component {
  static propTypes = {
    menuPosition: PropTypes.number,
    closingMenu: PropTypes.func.isRequired,
    closeMenu: PropTypes.func.isRequired,
    settings: PropTypes.object
  };

  static contextTypes = {
    router: PropTypes.object
  };

  static defaultProps = {
    settings: {}
  };

  render() {
    const { closeMenu, menuPosition: position, settings } = this.props;
    const isActive = this.context.router.isActive;
    const style = { transform: `translate3d(${position}px, 0, 0)` };
    const open = position > menuWidth * -1 && isMobile;
    const closed = position === menuWidth * -1 && isMobile;
    const coverStyle = {};

    if (isMobile) {
      style.zIndex = 15;
    }

    if (open) {
      coverStyle.display = 'block';
      coverStyle.backgroundColor = `rgba(0,0,0,${((menuWidth + position) / menuWidth) * 0.7})`;
    }

    if (position === menuWidth * -1) style.boxShadow = 'none';

    return (
      <div id="menu-wrapper">
        <div id="menu-cover" style={coverStyle} onClick={preventDefault(closeMenu)}></div>
        <div id="main-menu" style={style}>
          <CurrentUser />
          <ul>
            <MenuItem
              className={classNames({ active: isActive('tickets') })}
              closeMenu={closeMenu}
              label="Tickets"
              to={{ pathname: '/tickets', query: { setDefaultQuery: true } }}
              icon="svg/ticket.svg"
              alert={<TicketMenuAlert />}
              add={<AddTicket />}
            />

            <MenuItem
              className={classNames({ active: isActive('purchases') })}
              closeMenu={closeMenu}
              label="Purchases"
              to={{ pathname: '/purchases', query: { setDefaultQuery: true } }}
              icon="svg/expense-bill.svg"
              alert={<ExpenseMenuAlert />}
            />
          
            <MenuItem
              className={classNames({ active: isActive('time-reports') })}
              closeMenu={closeMenu}
              label="Time Reports"
              to={{ pathname: '/time-reports', query: { setDefaultQuery: true } }}
              icon="svg/time-report.svg"
              add={<AddTimeReport />}
            />
          
            <MenuItem
              className={classNames({ active: isActive('companies') })}
              closeMenu={closeMenu}
              label="Companies"
              to={{ pathname: '/companies', query: { setDefaultQuery: true } }}
              icon="svg/company.svg"
              add={<AddCompany />}
            />

            <MenuItem
              className={classNames({ active: isActive('people') })}
              closeMenu={closeMenu}
              label="People"
              to={{ pathname: '/people', query: { setDefaultQuery: true } }}
              icon="svg/person.svg"
              add={<AddPerson />}
            />

            <MenuItem
              className={classNames({ active: isActive('accounting') })}
              roles={PRAECE_OPERATIONS_ROLES}
              closed={closed}
              label="Accounting"
              icon="svg/invoice-list.svg"
              alert={<InvoiceMenuAlert />}
            >
              <MenuItem
                className={classNames({ active: isActive('accounting/billing') })}
                roles={PRAECE_OPERATIONS_ROLES}
                closeMenu={closeMenu}
                label="Billing"
                to={{ pathname: '/accounting/billing', query: { setDefaultQuery: true } }}
                icon="svg/billing-list.svg"
              />

              <MenuItem
                className={classNames({ active: isActive('accounting/invoices') })}
                roles={PRAECE_OPERATIONS_ROLES}
                closeMenu={closeMenu}
                label="Invoices"
                to={{ pathname: '/accounting/invoices', query: { setDefaultQuery: true } }}
                icon="svg/invoice-list.svg"
                alert={<InvoiceMenuAlert />}
              />

              <MenuItem
                className={classNames({ active: isActive('accounting/tax-rates') })}
                closeMenu={closeMenu}
                label="Tax Rates"
                to={{ pathname: '/accounting/tax-rates', query: { setDefaultQuery: true } }}
                icon="svg/invoice-list.svg"
              />
            </MenuItem>

            <MenuItem
              className={classNames({ active: isActive('admin') })}
              closed={closed}
              label="Admin"
              icon="svg/cog.svg"
            >
              <MenuItem
                className={classNames({ active: isActive('admin/users') })}
                closeMenu={closeMenu}
                label="Users"
                to={{ pathname: '/admin/users', query: { setDefaultQuery: true } }}
                icon="svg/person.svg"
              />

              <MenuItem
                className={classNames({ active: isActive('admin/email-list') })}
                closeMenu={closeMenu}
                label="Email List"
                to={{ pathname: '/admin/email-list', query: { setDefaultQuery: true } }}
                icon="svg/email.svg"
              />

              <MenuItem
                className={classNames({ active: isActive('admin/clients') })}
                closeMenu={closeMenu}
                label="Clients"
                to={{ pathname: '/admin/clients', query: { setDefaultQuery: true } }}
                icon="svg/company.svg"
              />

              <MenuItem
                className={classNames({ active: isActive('admin/xboard') })}
                closeMenu={closeMenu}
                label="xBoard"
                to={{ pathname: '/admin/xboard', query: { setDefaultQuery: true } }}
                icon="svg/checklist.svg"
              />

              <MenuItem
                className={classNames({ active: isActive('admin/agreements') })}
                roles={PRAECE_LEAD_ROLES}
                closeMenu={closeMenu}
                label="Agreements"
                to={{ pathname: '/admin/agreements', query: { setDefaultQuery: true } }}
                icon="svg/agreement.svg"
              />

              <MenuItem
                className={classNames({ active: isActive('admin/watches') })}
                roles={PRAECE_LEAD_ROLES}
                closeMenu={closeMenu}
                label="Watches"
                to={{ pathname: '/admin/watches', query: { setDefaultQuery: true } }}
                icon="svg/growth.svg"
              />

              <MenuItem
                className={classNames({ active: isActive('admin/triage') })}
                closeMenu={closeMenu}
                label="Triage"
                to={{ pathname: '/admin/triage', query: { setDefaultQuery: true } }}
                icon="svg/triage.svg"
              />

              <MenuItem
                className={classNames({ active: isActive('admin/settings') })}
                roles={PRAECE_ADMIN_ROLES}
                closeMenu={closeMenu}
                label="Settings"
                to={{ pathname: '/admin/settings' }}
                icon="svg/wrench.svg"
              />

              <MenuItem
                roles={PRAECE_ADMIN_ROLES}
                closeMenu={closeMenu}
                label="File Templates"
                link={getFolderOpenUrl(settings.templateFolder)}
                icon="svg/file-folder.svg"
              />
            </MenuItem>

            <MenuItem
              className={classNames({ active: isActive('mfa') })}
              closeMenu={closeMenu}
              label="MFA"
              to={{ pathname: '/mfa' }}
              icon="svg/shield.svg"
              trailing={<MyTeamPhone />}
            />

            <MenuItem
              label="Portal Updates"
              link="/portal-updates"
              internal
              alert={<PortalChangesMenuAlert />}
              icon="svg/info.svg"
            />

          </ul>

          <ul className="menu-footer">
            <MenuItem
              label="Send Feedback"
              link="mailto:portal-feedback@praece.com"
              icon="svg/feedback.svg"
            />
          </ul>
        </div>
      </div>
    );
  }
}
